import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  BlockStack,
  Text,
  RangeSlider,
  Select,
  Checkbox,
  TextField,
  InlineStack,
  Divider,
  Badge,
  Box,
  Collapsible,
  Button,
  Icon,
  Banner,
  Tooltip,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon, QuestionCircleIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { 
  RecommendationWeights, 
  ContextWeights, 
  EnabledTypes,
  defaultRecommendationWeights 
} from '../types';

interface RecommendationWeightsFormProps {
  weights: RecommendationWeights;
  onChange: (weights: RecommendationWeights) => void;
}

interface ContextCardProps {
  title: string;
  description: string;
  expectedImpact: string;
  context: ContextWeights;
  contextKey: keyof Pick<RecommendationWeights, 'productPage' | 'homePage' | 'cartPage' | 'recommendedForYou' | 'postPurchase' | 'email'>;
  onChange: (key: keyof Pick<RecommendationWeights, 'productPage' | 'homePage' | 'cartPage' | 'recommendedForYou' | 'postPurchase' | 'email'>, value: ContextWeights) => void;
}

const ContextCard: React.FC<ContextCardProps> = ({
  title,
  description,
  expectedImpact,
  context,
  contextKey,
  onChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleWeightChange = useCallback(
    (field: keyof ContextWeights, value: number | string | boolean) => {
      onChange(contextKey, { ...context, [field]: value });
    },
    [context, contextKey, onChange]
  );

  const total = context.similar + context.frequentlyBoughtTogether + context.trending + context.personalized + context.newArrivals;
  const isValidTotal = context.mode !== 'mix' || total === 100;

  const modeOptions = [
    { label: t('weights.modeMix'), value: 'mix' },
    { label: t('weights.modeSimilar'), value: 'similar' },
    { label: t('weights.modeFBT'), value: 'fbt' },
    { label: t('weights.modeTrending'), value: 'trending' },
    { label: t('weights.modePersonalized'), value: 'personalized' },
    { label: t('weights.modeNewArrivals'), value: 'newarrivals' },
  ];

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingMd">{title}</Text>
              <Checkbox
                label=""
                checked={context.enabled}
                onChange={(checked) => handleWeightChange('enabled', checked)}
              />
            </InlineStack>
            <Text as="p" tone="subdued" variant="bodySm">{description}</Text>
          </BlockStack>
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="info">{expectedImpact}</Badge>
            <Button
              variant="plain"
              onClick={() => setIsOpen(!isOpen)}
              icon={<Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} />}
            />
          </InlineStack>
        </InlineStack>

        <Collapsible open={isOpen} id={`collapsible-${contextKey}`}>
          <BlockStack gap="400">
            <Divider />
            
            <Select
              label={t('weights.modeLabel')}
              options={modeOptions}
              value={context.mode}
              onChange={(value) => handleWeightChange('mode', value)}
            />

            {context.mode === 'mix' && (
              <>
                {!isValidTotal && (
                  <Banner tone="warning">
                    {t('weights.totalWarning', { total })}
                  </Banner>
                )}

                <BlockStack gap="300">
                  <RangeSlider
                    label={t('weights.similarLabel', { value: context.similar })}
                    helpText={t('weights.similarHelp')}
                    value={context.similar}
                    onChange={(value) => handleWeightChange('similar', value as number)}
                    min={0}
                    max={100}
                    step={5}
                    output
                  />
                  <RangeSlider
                    label={t('weights.fbtLabel', { value: context.frequentlyBoughtTogether })}
                    helpText={t('weights.fbtHelp')}
                    value={context.frequentlyBoughtTogether}
                    onChange={(value) => handleWeightChange('frequentlyBoughtTogether', value as number)}
                    min={0}
                    max={100}
                    step={5}
                    output
                  />
                  <RangeSlider
                    label={t('weights.trendingLabel', { value: context.trending })}
                    helpText={t('weights.trendingHelp')}
                    value={context.trending}
                    onChange={(value) => handleWeightChange('trending', value as number)}
                    min={0}
                    max={100}
                    step={5}
                    output
                  />
                  <RangeSlider
                    label={t('weights.personalizedLabel', { value: context.personalized })}
                    helpText={t('weights.personalizedHelp')}
                    value={context.personalized}
                    onChange={(value) => handleWeightChange('personalized', value as number)}
                    min={0}
                    max={100}
                    step={5}
                    output
                  />
                  <RangeSlider
                    label={t('weights.newArrivalsLabel', { value: context.newArrivals })}
                    helpText={t('weights.newArrivalsHelp')}
                    value={context.newArrivals}
                    onChange={(value) => handleWeightChange('newArrivals', value as number)}
                    min={0}
                    max={100}
                    step={5}
                    output
                  />
                </BlockStack>
              </>
            )}

          </BlockStack>
        </Collapsible>
      </BlockStack>
    </Card>
  );
};

export const RecommendationWeightsForm: React.FC<RecommendationWeightsFormProps> = ({
  weights,
  onChange,
}) => {
  const { t } = useTranslation();
  const handleContextChange = useCallback(
    (key: keyof Pick<RecommendationWeights, 'productPage' | 'homePage' | 'cartPage' | 'recommendedForYou' | 'postPurchase' | 'email'>, value: ContextWeights) => {
      onChange({ ...weights, [key]: value });
    },
    [weights, onChange]
  );

  const handleGlobalChange = useCallback(
    (field: keyof RecommendationWeights, value: number | string | EnabledTypes) => {
      onChange({ ...weights, [field]: value });
    },
    [weights, onChange]
  );

  const handleEnabledTypeChange = useCallback(
    (type: keyof EnabledTypes, enabled: boolean) => {
      const current = weights.enabledTypes || defaultRecommendationWeights.enabledTypes!;
      onChange({
        ...weights,
        enabledTypes: { ...current, [type]: enabled },
      });
    },
    [weights, onChange]
  );

  const fallbackOptions = [
    { label: t('weights.fallbackTrending'), value: 'trending' },
    { label: t('weights.fallbackBestSellers'), value: 'bestsellers' },
    { label: t('weights.fallbackNewArrivals'), value: 'newarrivals' },
    { label: t('weights.fallbackRandom'), value: 'random' },
    { label: t('weights.fallbackHide'), value: 'hide' },
  ];

  return (
    <BlockStack gap="500">
      {/* Global Settings */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingLg">{t('weights.globalTitle')}</Text>
          <Text as="p" tone="subdued">
            {t('weights.globalDesc')}
          </Text>

          <Divider />

          <BlockStack gap="400">
            <RangeSlider
              label={t('weights.aggressivenessLabel', { value: weights.globalAggressiveness ?? 60 })}
              helpText={t('weights.aggressivenessHelp')}
              value={weights.globalAggressiveness ?? 60}
              onChange={(value) => handleGlobalChange('globalAggressiveness', value as number)}
              min={0}
              max={100}
              step={5}
              output
            />

            <RangeSlider
              label={t('weights.discoveryLabel', { value: weights.discoveryLevel ?? 40 })}
              helpText={t('weights.discoveryHelp')}
              value={weights.discoveryLevel ?? 40}
              onChange={(value) => handleGlobalChange('discoveryLevel', value as number)}
              min={0}
              max={100}
              step={5}
              output
            />
          </BlockStack>

          <Divider />

          <Text as="h3" variant="headingMd">{t('weights.enabledTypesTitle')}</Text>
          <InlineStack gap="400" wrap>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typeSimilar')}
                checked={weights.enabledTypes?.similar ?? true}
                onChange={(checked) => handleEnabledTypeChange('similar', checked)}
              />
              <Tooltip content={t('weights.typeSimilarHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typeFBT')}
                checked={weights.enabledTypes?.frequentlyBoughtTogether ?? true}
                onChange={(checked) => handleEnabledTypeChange('frequentlyBoughtTogether', checked)}
              />
              <Tooltip content={t('weights.typeFBTHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typeTrending')}
                checked={weights.enabledTypes?.trending ?? true}
                onChange={(checked) => handleEnabledTypeChange('trending', checked)}
              />
              <Tooltip content={t('weights.typeTrendingHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typePersonalized')}
                checked={weights.enabledTypes?.personalized ?? true}
                onChange={(checked) => handleEnabledTypeChange('personalized', checked)}
              />
              <Tooltip content={t('weights.typePersonalizedHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typeNewArrivals')}
                checked={weights.enabledTypes?.newArrivals ?? true}
                onChange={(checked) => handleEnabledTypeChange('newArrivals', checked)}
              />
              <Tooltip content={t('weights.typeNewArrivalsHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              <Checkbox
                label={t('weights.typeBestSellers')}
                checked={weights.enabledTypes?.bestSellers ?? true}
                onChange={(checked) => handleEnabledTypeChange('bestSellers', checked)}
              />
              <Tooltip content={t('weights.typeBestSellersHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
            </InlineStack>
          </InlineStack>

          <Divider />

          <InlineStack gap="400">
            <Box minWidth="200px">
              <Select
                label={t('weights.fallbackLabel')}
                helpText={t('weights.fallbackHelp')}
                options={fallbackOptions}
                value={weights.fallbackType ?? 'trending'}
                onChange={(value) => handleGlobalChange('fallbackType', value)}
              />
            </Box>
            <Box minWidth="120px">
              <TextField
                label={t('weights.minRecommendationsLabel')}
                type="number"
                value={String(weights.minRecommendations ?? 2)}
                onChange={(value) => handleGlobalChange('minRecommendations', parseInt(value) || 2)}
                autoComplete="off"
                min={1}
                max={12}
              />
            </Box>
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Context-specific Settings */}
      <Text as="h2" variant="headingLg">{t('weights.contextSettings')}</Text>
      <Text as="p" tone="subdued">
        {t('weights.contextSettingsDesc')}
      </Text>

      <ContextCard
        title={t('weights.productPage.title')}
        description={t('weights.productPage.description')}
        expectedImpact={t('weights.productPage.impact')}
        context={weights.productPage || defaultRecommendationWeights.productPage!}
        contextKey="productPage"
        onChange={handleContextChange}
      />

      <ContextCard
        title={t('weights.homePage.title')}
        description={t('weights.homePage.description')}
        expectedImpact={t('weights.homePage.impact')}
        context={weights.homePage || defaultRecommendationWeights.homePage!}
        contextKey="homePage"
        onChange={handleContextChange}
      />

      <ContextCard
        title={t('weights.cartPage.title')}
        description={t('weights.cartPage.description')}
        expectedImpact={t('weights.cartPage.impact')}
        context={weights.cartPage || defaultRecommendationWeights.cartPage!}
        contextKey="cartPage"
        onChange={handleContextChange}
      />

      <ContextCard
        title={t('weights.recommendedForYou.title')}
        description={t('weights.recommendedForYou.description')}
        expectedImpact={t('weights.recommendedForYou.impact')}
        context={weights.recommendedForYou || defaultRecommendationWeights.recommendedForYou!}
        contextKey="recommendedForYou"
        onChange={handleContextChange}
      />

      <ContextCard
        title={t('weights.postPurchase.title')}
        description={t('weights.postPurchase.description')}
        expectedImpact={t('weights.postPurchase.impact')}
        context={weights.postPurchase || defaultRecommendationWeights.postPurchase!}
        contextKey="postPurchase"
        onChange={handleContextChange}
      />

      <ContextCard
        title={t('weights.emailRecommendations.title')}
        description={t('weights.emailRecommendations.description')}
        expectedImpact={t('weights.emailRecommendations.impact')}
        context={weights.email || defaultRecommendationWeights.email!}
        contextKey="email"
        onChange={handleContextChange}
      />
    </BlockStack>
  );
};
